// src/services/paystack.service.ts
import { ENV } from "../config/environment";
import { PAYMENT_CONFIG, PAYMENT_MESSAGES, PAYMENT_ROUTES } from "../config/payment.config";
import { paymentCalculator } from "./payment-calculator.service";
import { paymentStateMachine } from "./payment-state.service";
import { PaymentErrorHandler } from "../utils/payment-errors";
import { paymentService } from "./payment.service";
import toast from "react-hot-toast";

declare global {
  interface Window {
    PaystackPop: {
      setup: (config: any) => {
        openIframe: () => void;
      };
    };
  }
}

export interface PaystackConfig {
  cardId: string;
  email: string;
  userId: string;
  bouquetAmount?: number;
  extraContribution?: number; // Add this
  metadata?: Record<string, any>;
}

export class PaystackService {
  private static instance: PaystackService;
  private publicKey: string;
  private navigate: ((path: string) => void) | null = null;
  private scriptLoaded = false;
  private scriptLoading = false;
  private scriptLoadPromise: Promise<void> | null = null;
  private currentUserId: string | null = null;

  private constructor() {
    const isProduction = process.env.NODE_ENV === "production";

    if (isProduction) {
      if (!ENV.PAYSTACK_PUBLIC_LIVE_KEY) {
        throw new Error("PAYSTACK_PUBLIC_LIVE_KEY is required in production");
      }
      this.publicKey = ENV.PAYSTACK_PUBLIC_LIVE_KEY;
    } else {
      if (!ENV.PAYSTACK_PUBLIC_TEST_KEY) {
        throw new Error("PAYSTACK_PUBLIC_TEST_KEY is required in development");
      }
      this.publicKey = ENV.PAYSTACK_PUBLIC_TEST_KEY;
    }

    this.loadScript();
  }

  static getInstance(): PaystackService {
    if (!PaystackService.instance) {
      PaystackService.instance = new PaystackService();
    }
    return PaystackService.instance;
  }

  setNavigate(navigate: (path: string) => void) {
    this.navigate = navigate;
  }

  // Add this method
  setCurrentUser(userId: string | null) {
    this.currentUserId = userId;
  }

  private async loadScript(): Promise<void> {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector(
      'script[src="https://js.paystack.co/v1/inline.js"]'
    );
    if (existingScript) {
      this.scriptLoaded = true;
      return Promise.resolve();
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      // Add a timeout to prevent hanging
      const timeout = setTimeout(() => {
        reject(new Error("Paystack script loading timed out"));
      }, 10000);

      if (window.PaystackPop) {
        clearTimeout(timeout);
        this.scriptLoaded = true;
        resolve();
        return;
      }

      this.scriptLoading = true;
      const script = document.createElement("script");
      script.src = "https://js.paystack.co/v1/inline.js";
      script.async = true;

      script.onload = () => {
        clearTimeout(timeout);
        this.scriptLoaded = true;
        this.scriptLoading = false;
        resolve();
      };

      script.onerror = () => {
        clearTimeout(timeout);
        this.scriptLoading = false;
        this.scriptLoadPromise = null;
        reject(new Error("Failed to load Paystack script"));
      };

      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  async initializePayment(config: PaystackConfig): Promise<void> {
    try {
      await paymentStateMachine.transition("initializing");

      // Validate amounts with proper defaults
      const bouquetAmount = config.bouquetAmount || 0;
      const extraContribution = config.extraContribution || 0;

      // Only validate bouquet amount if it's greater than 0
      // This allows cards without bouquets to proceed
      if (bouquetAmount > 0 && !paymentCalculator.validateBouquetAmount(bouquetAmount)) {
        throw new Error("Invalid bouquet amount");
      }

      if (!paymentCalculator.validateExtraContribution(extraContribution)) {
        throw new Error("Invalid extra contribution amount");
      }

      // Calculate breakdown with extra contribution
      const breakdown = paymentCalculator.calculatePaymentBreakdown(
        bouquetAmount,
        extraContribution
      );

      // Only proceed if total is greater than 0
      if (breakdown.total <= 0) {
        throw new Error("Payment amount must be greater than 0");
      }

      const amountInKobo = Math.round(breakdown.total * 100);
      const reference = `LRNSY-${config.cardId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Load Paystack script
      await this.loadScript();

      if (!window.PaystackPop) {
        throw new Error("Paystack not loaded");
      }

      await paymentStateMachine.transition("processing");

      const paystackConfig = {
        key: this.publicKey,
        email: config.email,
        amount: amountInKobo,
        currency: PAYMENT_CONFIG.currency,
        ref: reference,
        metadata: {
          card_id: config.cardId,
          user_id: config.userId,
          bouquet_amount: bouquetAmount,
          platform_fee: breakdown.basePlatformFee,
          vat: breakdown.vat,
          total: breakdown.total,
          timestamp: Date.now(),
          ...config.metadata,
        },
        callback: (response: any) => this.handlePaymentSuccess(config, response),
        onClose: () => this.handlePaymentClose(),
      };

      const handler = window.PaystackPop.setup(paystackConfig);
      handler.openIframe();
    } catch (error) {
      await paymentStateMachine.transition("failed");
      const handledError = PaymentErrorHandler.handle(error);

      toast.error(handledError.message);
      throw handledError;
    }
  }

  private async handlePaymentSuccess(config: PaystackConfig, response: any): Promise<void> {
    try {
      const providerRef = response.reference || response.ref;

      if (!providerRef) {
        throw new Error("Payment reference is missing");
      }

      // Safely handle undefined values with defaults
      const bouquetAmount = config.bouquetAmount ?? 0;
      const extraContribution = config.extraContribution ?? 0;

      // Calculate breakdown with safe values
      const breakdown = paymentCalculator.calculatePaymentBreakdown(
        bouquetAmount,
        extraContribution
      );

      // Record payment - this will update the card status
      await paymentService.recordPayment(
        {
          cardId: config.cardId,
          email: config.email,
          amount: breakdown.total,
          currency: PAYMENT_CONFIG.currency,
          provider: "paystack",
          providerRef,
          metadata: {
            paystackReference: providerRef,
            paystackTransaction: response.transaction || response.trxref,
            paystackStatus: response.status,
            bouquetAmount: bouquetAmount,
            extraContribution: extraContribution,
            timestamp: new Date().toISOString(),
            ...config.metadata,
          },
        },
        config.userId,
        config.email
      );

      await paymentStateMachine.transition("success");

      toast.success(PAYMENT_MESSAGES.SUCCESS);

      // Navigate after delay
      setTimeout(() => {
        if (this.navigate) {
          this.navigate(`/share/${config.cardId}`);
        } else {
          window.location.href = `/share/${config.cardId}`;
        }
      }, PAYMENT_ROUTES.SUCCESS_DELAY || 2000);
    } catch (error) {
      await paymentStateMachine.transition("failed");
      console.error("Payment success handler error:", error);

      // Still try to navigate
      setTimeout(() => {
        if (this.navigate) {
          this.navigate(`/share/${config.cardId}`);
        } else {
          window.location.href = `/share/${config.cardId}`;
        }
      }, 3000);
    }
  }

  private handlePaymentClose(): void {
    paymentStateMachine.transition("cancelled");
    toast.error(PAYMENT_MESSAGES.CANCELLED);
  }

  async verifyPayment(reference: string): Promise<any> {
    try {
      // Implement actual verification with Paystack API if needed
      return { verified: true, reference };
    } catch (error) {
      const handledError = PaymentErrorHandler.handle(error);
      toast.error(handledError.message);
      throw handledError;
    }
  }

  reset(): void {
    paymentStateMachine.reset();
  }
}

export const paystackService = PaystackService.getInstance();
// test change
